import { Link, Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api } from './lib/api.js'
import { signOut } from './lib/auth.js'
import type {
  EngineResponse,
  EngineSection,
  EngineUptimeWindow,
  OptInType,
  PendingMaintenanceAction,
} from '@cloudcat-apps/admin-shared'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

/**
 * The date RDS will act on for one pending action, in the order RDS itself
 * prefers. Shared by the summary line and the table row so the two can never
 * cite different dates for the same action.
 */
function nextApplyDate(action: PendingMaintenanceAction): string | null {
  return action.currentApplyDate ?? action.autoAppliedAfterDate ?? action.forcedApplyDate
}

/**
 * The version a pending action would move the cluster to, dug out of RDS's own
 * description. RDS ships no structured field for it — `system-update` reads
 * like "New Aurora minor version Aurora PostgreSQL 17.9", so the target only
 * ever exists in the prose, and `pendingVersion` stays null until the change is
 * actually in flight.
 *
 * Requires the description to say "version" before trusting a dotted number, so
 * a stray "within 14 days" style figure cannot be presented as an upgrade
 * target. Null when the description names none — a certificate rotation or
 * hardware maintenance genuinely changes no version, and the description is
 * rendered beside this so a bad guess is visible rather than authoritative.
 */
function targetEngineVersion(action: PendingMaintenanceAction): string | null {
  const description = action.description
  if (!description || !/\bversions?\b/i.test(description)) return null
  return /\d+(?:\.\d+)+/.exec(description)?.[0] ?? null
}

/**
 * What RDS will do with this action as things stand. `optInStatus` is the
 * authoritative field; the dates only say when.
 */
function describeOptInState(action: PendingMaintenanceAction): string {
  const status = action.optInStatus
  if (status === 'immediate') return 'Applying now'
  if (status === 'next-maintenance') return 'Scheduled for the next window'
  if (status === 'undo-opt-in') return 'Opt-in withdrawn'
  if (status) return status
  if (action.autoAppliedAfterDate || action.forcedApplyDate) {
    return 'Not opted in — RDS will apply it on its own'
  }
  return 'Not opted in'
}

/**
 * Whether withdrawing the opt-in is still possible. RDS only accepts
 * `undo-opt-in` while the action has not started, and an action already
 * applying `immediate` is past the point of recall.
 */
function canUndo(action: PendingMaintenanceAction): boolean {
  return !!action.optInStatus && action.optInStatus !== 'immediate'
}

/**
 * Per-type confirm text. "Apply now" has to name its consequence: the writer
 * runs at zero ACUs and auto-pauses, so applying immediately wakes it and
 * interrupts the cluster there and then rather than at 03:14 on a Sunday.
 */
function confirmCopy(action: PendingMaintenanceAction, optInType: OptInType): string {
  const what = `"${action.action}" on ${action.resourceIdentifier}`
  if (optInType === 'immediate') {
    return `Apply ${what} immediately?\n\nThis wakes the cluster from auto-pause and interrupts it now, not at the next maintenance window.`
  }
  if (optInType === 'undo-opt-in') {
    return `Withdraw the opt-in for ${what}?\n\nRDS goes back to whatever it would have done on its own.`
  }
  return `Schedule ${what} for the next maintenance window?`
}

/** Human labels for the response sections that can degrade independently. */
const sectionLabels: Record<EngineSection, string> = {
  upgradeTargets: 'Available upgrade targets',
  pendingActions: 'Pending maintenance actions',
  recentEvents: 'Recent RDS events',
  history: 'Recorded version history',
}

/** `degraded` is a plain string array on the wire; fall back to the raw key. */
function sectionLabel(section: string): string {
  return sectionLabels[section as EngineSection] ?? section
}

/**
 * Marks a section whose backing read failed, so an empty table below is not
 * read as "nothing pending".
 */
function DegradedNote({ engine, section }: { engine: EngineResponse; section: EngineSection }) {
  if (!engine.degraded.includes(section)) return null
  return (
    <p className="muted">
      <span className="drift-badge">not loaded</span> This section&rsquo;s read failed, so it is
      empty for that reason — not because there is nothing to show.
    </p>
  )
}

/**
 * Compares dot-separated numeric version strings. Returns a positive number
 * when `a` is newer than `b`, negative when older, 0 when equal.
 */
function compareVersions(a: string, b: string): number {
  const left = a.split('.').map(Number)
  const right = b.split('.').map(Number)
  for (let i = 0; i < Math.max(left.length, right.length); i += 1) {
    const l = left[i] ?? 0
    const r = right[i] ?? 0
    if (!Number.isFinite(l) || !Number.isFinite(r)) return NaN
    if (l !== r) return l - r
  }
  return 0
}

/**
 * Drift is two different situations. Live ahead of the pin is the dangerous one:
 * CloudFormation cannot walk an engine version backwards, so the next deploy
 * touching the cluster fails. Live behind the pin is the ordinary state between
 * bumping the pin and deploying it, so it must not claim the same alarm.
 */
function describeVersionDrift(engine: EngineResponse): string | null {
  if (!engine.versionDrift) return null
  const order = compareVersions(engine.currentVersion, engine.pinnedVersion)
  if (order > 0) {
    return 'drift — live is ahead of the pin; CDK cannot walk the engine version backwards'
  }
  if (order < 0) return 'drift — live is behind the pin; the cluster has not been deployed yet'
  return 'drift — the live version does not match the pin'
}

/**
 * One-line summary of what RDS will do next. Pending actions are the real
 * answer; the weekly window is only when maintenance *could* land.
 */
function describeNextMaintenance(engine: EngineResponse): string {
  const dates = engine.pendingActions.map(nextApplyDate).filter((date): date is string => !!date)

  if (dates.length > 0) {
    const soonest = [...dates].sort()[0]
    const applying = dates.filter((date) => date === soonest).length
    return `${applying} of ${engine.pendingActions.length} actions applies from ${soonest}`
  }
  if (engine.pendingActions.length > 0) {
    const plural = engine.pendingActions.length === 1 ? 'action' : 'actions'
    return `${engine.pendingActions.length} ${plural} pending with no scheduled date yet`
  }
  if (engine.preferredMaintenanceWindow) {
    return `Nothing scheduled. Weekly window: ${engine.preferredMaintenanceWindow} (UTC)`
  }
  return 'Nothing scheduled and no maintenance window reported'
}

/** Share of sampled time awake, e.g. "37.5%"; an em dash with no samples. */
function formatPercent(ratio: number | null): string {
  if (ratio === null) return '—'
  return `${(ratio * 100).toFixed(1)}%`
}

/** Whole minutes as "2d 3h", "3h 20m" or "45m". */
function formatDuration(minutes: number): string {
  const total = Math.round(minutes)
  const days = Math.floor(total / 1440)
  const hours = Math.floor((total % 1440) / 60)
  const mins = total % 60
  if (days > 0) return `${days}d ${hours}h`
  if (hours > 0) return `${hours}h ${mins}m`
  return `${mins}m`
}

/** "24 hours" for the day window, "7 days" / "30 days" for the rest. */
function uptimeWindowLabel(hours: number): string {
  return hours % 24 === 0 && hours > 24 ? `${hours / 24} days` : `${hours} hours`
}

/**
 * Flags a window whose CloudWatch samples cover noticeably less than its span,
 * so a ratio over a few hours of data is not read as a month's figure.
 */
function describeCoverage(window: EngineUptimeWindow): string | null {
  if (window.expectedMinutes === 0) return null
  const coverage = window.sampledMinutes / window.expectedMinutes
  if (coverage >= 0.95) return null
  return `samples cover ${formatDuration(window.sampledMinutes)} of ${formatDuration(window.expectedMinutes)}`
}

/** Hand a Blob to the browser as a file download. */
function saveBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName
  link.click()
  // Deferred: revoking in the same tick can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 0)
}

/**
 * How much of the time the writer runs versus sits auto-paused. Its own query,
 * so a CloudWatch failure never blanks the version fields above it.
 */
function AvailabilitySection() {
  const q = useQuery({ queryKey: ['engine-uptime'], queryFn: () => api.engineUptime() })
  return (
    <>
      <h2 className="section-title">Availability</h2>
      <p className="muted">
        From CloudWatch <code>ServerlessDatabaseCapacity</code> in 5-minute slots. A slot counts as
        awake if the writer ran above 0 ACUs at any point in it.
      </p>
      {q.isLoading && <p className="muted">Loading…</p>}
      {q.error && <p role="alert">Failed to load availability: {String(q.error)}</p>}
      {q.data && (
        <>
          <p>
            <code>{q.data.instanceIdentifier}</code> is{' '}
            {q.data.currentlyAwake === null
              ? 'of unknown state (no samples)'
              : q.data.currentlyAwake
                ? 'awake'
                : 'paused'}
            {q.data.latestSampleAt && (
              <span className="muted"> · as of {q.data.latestSampleAt}</span>
            )}
          </p>
          <p className="muted">
            Last woke: {q.data.lastWokeAt ?? '—'} · Last paused: {q.data.lastPausedAt ?? '—'}
          </p>
          <table>
            <thead>
              <tr>
                <th>Window</th>
                <th>Awake</th>
                <th>Time awake</th>
                <th>Wake-ups</th>
              </tr>
            </thead>
            <tbody>
              {q.data.windows.map((w) => (
                <tr key={w.hours}>
                  <td>Last {uptimeWindowLabel(w.hours)}</td>
                  <td>
                    {formatPercent(w.awakeRatio)}
                    {describeCoverage(w) && <div className="muted">{describeCoverage(w)}</div>}
                  </td>
                  <td>{formatDuration(w.awakeMinutes)}</td>
                  <td>{w.wakeCount}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="muted">Hours awake per UTC day, last 30 days:</p>
          <div className="uptime-bars" role="img" aria-label="Hours awake per day">
            {q.data.daily.map((d) => (
              <div
                key={d.date}
                className="uptime-bar"
                title={`${d.date}: ${formatDuration(d.awakeMinutes)} awake${
                  d.sampledMinutes === 0 ? ' (no samples)' : ''
                }`}
              >
                <div
                  className="uptime-bar-fill"
                  style={{ height: `${Math.min(100, (d.awakeMinutes / 1440) * 100)}%` }}
                />
              </div>
            ))}
          </div>
        </>
      )}
    </>
  )
}

/**
 * The writer's Postgres log files — the same list as the RDS console's Logs
 * tab — with a per-file download.
 */
function LogFilesSection() {
  const q = useQuery({ queryKey: ['engine-logs'], queryFn: () => api.engineLogs() })
  const [busyFile, setBusyFile] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  async function download(fileName: string) {
    setBusyFile(fileName)
    setDownloadError(null)
    try {
      saveBlob(await api.engineLogFile(fileName), fileName.replaceAll('/', '_'))
    } catch (err) {
      setDownloadError(`Failed to download ${fileName}: ${String(err)}`)
    } finally {
      setBusyFile(null)
    }
  }

  return (
    <>
      <h2 className="section-title">Postgres log files</h2>
      <p className="muted">
        Downloads over 4 MB are cut off with a note at the end; fetch those from the RDS console.
        Logs can contain query text.
      </p>
      {q.isLoading && <p className="muted">Loading…</p>}
      {q.error && <p role="alert">Failed to load log files: {String(q.error)}</p>}
      {downloadError && <p role="alert">{downloadError}</p>}
      {q.data && (
        <>
          <p className="muted">
            <code>{q.data.instanceIdentifier}</code> · {q.data.files.length} files ·{' '}
            {formatBytes(q.data.totalSize)} total · newest written {q.data.newestWrittenAt ?? '—'}
          </p>
          {q.data.files.length === 0 ? (
            <p className="muted">No log files.</p>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>File</th>
                  <th>Size</th>
                  <th>Last written</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {q.data.files.map((f) => (
                  <tr key={f.fileName}>
                    <td>
                      <code>{f.fileName}</code>
                    </td>
                    <td>{formatBytes(f.size)}</td>
                    <td>{f.lastWritten}</td>
                    <td>
                      <button disabled={busyFile !== null} onClick={() => download(f.fileName)}>
                        {busyFile === f.fileName ? 'Downloading…' : 'Download'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </>
  )
}

const rootRoute = createRootRoute({
  component: function Root() {
    return (
      <>
        <nav className="topnav">
          <strong>cloudcat-apps admin</strong>
          <Link to="/">Overview</Link>
          <Link to="/databases">Databases</Link>
          <Link to="/environments">Environments</Link>
          <Link to="/engine">Engine</Link>
          <Link to="/dumps">Dumps</Link>
          <Link to="/sessions">Sessions</Link>
          <span style={{ flex: 1 }} />
          <button onClick={() => void signOut()}>Sign out</button>
        </nav>
        <div className="app">
          <Outlet />
        </div>
      </>
    )
  },
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: function Index() {
    const me = useQuery({ queryKey: ['me'], queryFn: () => api.me() })
    return (
      <>
        <h1>Overview</h1>
        {me.isLoading && <p className="muted">Loading…</p>}
        {me.error && <p role="alert">Failed to load: {String(me.error)}</p>}
        {me.data && (
          <p className="muted">
            Signed in as <code>{me.data.username}</code> · groups:{' '}
            <code>{me.data.groups.join(', ') || '—'}</code>
          </p>
        )}
        <p>
          Pick a page from the nav. <Link to="/databases">Databases</Link> is a good starting point.
        </p>
      </>
    )
  },
})

const databasesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/databases',
  component: function Databases() {
    const q = useQuery({ queryKey: ['databases'], queryFn: () => api.databases() })
    const drift = q.data?.drift
    return (
      <>
        <h1>Databases</h1>
        {q.isLoading && <p className="muted">Loading…</p>}
        {q.error && <p role="alert">Failed to load: {String(q.error)}</p>}
        {q.data && (
          <>
            <p className="muted">
              Registry rows: {q.data.registry.length} · pg_database entries:{' '}
              {q.data.pgDatabases.length}
              {drift && (drift.orphans.length > 0 || drift.dangling.length > 0) && (
                <span className="drift-badge">drift detected</span>
              )}
            </p>
            <h2 className="section-title">Registry (DynamoDB)</h2>
            <table>
              <thead>
                <tr>
                  <th>Database</th>
                  <th>App</th>
                  <th>Role</th>
                  <th>Purpose</th>
                  <th>Created</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {q.data.registry.map((r) => (
                  <tr key={r.databaseName}>
                    <td>
                      <code>{r.databaseName}</code>
                    </td>
                    <td>{r.appName}</td>
                    <td>{r.roleName}</td>
                    <td>{r.purpose ?? '—'}</td>
                    <td>{r.createdAt}</td>
                    <td>{r.expiresAt ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <h2 className="section-title">pg_database (live)</h2>
            <ul>
              {q.data.pgDatabases.map((n) => {
                const isOrphan = drift?.orphans.includes(n)
                return (
                  <li key={n}>
                    <code>{n}</code>
                    {isOrphan && <span className="drift-badge">orphan (no registry row)</span>}
                  </li>
                )
              })}
            </ul>
            {drift && drift.dangling.length > 0 && (
              <>
                <h2 className="section-title">Dangling registry rows</h2>
                <p className="muted">
                  These have registry rows but no matching pg_database — likely failed
                  provision/drop.
                </p>
                <ul>
                  {drift.dangling.map((n) => (
                    <li key={n}>
                      <code>{n}</code>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </>
        )}
      </>
    )
  },
})

const environmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/environments',
  component: function Environments() {
    const q = useQuery({ queryKey: ['environments'], queryFn: () => api.environments() })
    return (
      <>
        <h1>Environments</h1>
        {q.isLoading && <p className="muted">Loading…</p>}
        {q.error && <p role="alert">Failed to load: {String(q.error)}</p>}
        {q.data?.apps.map((app) => (
          <div key={app.appName}>
            <h2 className="section-title">{app.appName}</h2>
            <table>
              <thead>
                <tr>
                  <th>Database</th>
                  <th>Role</th>
                  <th>Purpose</th>
                  <th>Created</th>
                  <th>Expires</th>
                </tr>
              </thead>
              <tbody>
                {app.databases.map((r) => (
                  <tr key={r.databaseName}>
                    <td>
                      <code>{r.databaseName}</code>
                    </td>
                    <td>{r.roleName}</td>
                    <td>{r.purpose ?? '—'}</td>
                    <td>{r.createdAt}</td>
                    <td>{r.expiresAt ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </>
    )
  },
})

const dumpsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dumps',
  component: function Dumps() {
    const dbs = useQuery({ queryKey: ['databases'], queryFn: () => api.databases() })
    const [selected, setSelected] = useState<string | null>(null)
    const dumps = useQuery({
      queryKey: ['dumps', selected],
      queryFn: () => api.dumps(selected as string),
      enabled: !!selected,
    })
    return (
      <>
        <h1>Dumps</h1>
        <p className="muted">Pick a database to list its backups.</p>
        {dbs.data && (
          <select
            value={selected ?? ''}
            onChange={(e) => setSelected(e.target.value || null)}
            style={{ padding: '0.4rem', marginBottom: '1rem' }}
          >
            <option value="">— choose a database —</option>
            {dbs.data.registry.map((r) => (
              <option key={r.databaseName} value={r.databaseName}>
                {r.databaseName} ({r.appName})
              </option>
            ))}
          </select>
        )}
        {selected && (
          <div style={{ marginBottom: '1rem' }}>
            <button
              onClick={async () => {
                await api.triggerDump(selected)
                await dumps.refetch()
              }}
            >
              Trigger dump now
            </button>
          </div>
        )}
        {dumps.isLoading && <p className="muted">Loading…</p>}
        {dumps.data && (
          <table>
            <thead>
              <tr>
                <th>Key</th>
                <th>Size</th>
                <th>Last modified</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {dumps.data.dumps.map((d) => (
                <tr key={d.key}>
                  <td>
                    <code>{d.key}</code>
                  </td>
                  <td>{formatBytes(d.size)}</td>
                  <td>{d.lastModified}</td>
                  <td>
                    <button
                      onClick={async () => {
                        const { url } = await api.dumpUrl(d.key)
                        window.open(url, '_blank', 'noopener,noreferrer')
                      }}
                    >
                      Download
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    )
  },
})

const sessionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/sessions',
  component: function Sessions() {
    const q = useQuery({ queryKey: ['sessions'], queryFn: () => api.sessions() })
    const [signOutError, setSignOutError] = useState<string | null>(null)
    const [signOutOk, setSignOutOk] = useState<string | null>(null)
    return (
      <>
        <h1>Sessions</h1>
        <p className="muted">
          Sign-in is federated through Google and tokens are stateless, so there is no per-session
          list. “Sign out everywhere” revokes a user’s refresh tokens; already-issued tokens stay
          valid for up to an hour.
        </p>
        {q.isLoading && <p className="muted">Loading…</p>}
        {q.error && <p role="alert">Failed to load: {String(q.error)}</p>}
        {signOutError && <p role="alert">{signOutError}</p>}
        {signOutOk && <p className="muted">{signOutOk}</p>}
        {q.data && (
          <table>
            <thead>
              <tr>
                <th>Email/Username</th>
                <th>Status</th>
                <th>Enabled</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {q.data.users.map((user) => (
                <tr key={user.username}>
                  <td>
                    <code>{user.email ?? user.username}</code>
                  </td>
                  <td>{user.status}</td>
                  <td>{user.enabled ? 'Yes' : 'No'}</td>
                  <td>
                    <button
                      onClick={async () => {
                        const who = user.email ?? user.username
                        if (!window.confirm(`Sign ${who} out everywhere?`)) return
                        try {
                          setSignOutError(null)
                          setSignOutOk(null)
                          await api.signOutUser(user.username)
                          setSignOutOk(`Signed out ${who}`)
                        } catch (err) {
                          setSignOutError(`Failed to sign out ${who}: ${String(err)}`)
                        }
                      }}
                    >
                      Sign out everywhere
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </>
    )
  },
})

const engineRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/engine',
  component: function Engine() {
    const q = useQuery({ queryKey: ['engine'], queryFn: () => api.engine() })
    const [applyBusy, setApplyBusy] = useState(false)
    const [applyError, setApplyError] = useState<string | null>(null)
    const [applyOk, setApplyOk] = useState<string | null>(null)

    async function applyOptIn(action: PendingMaintenanceAction, optInType: OptInType) {
      if (!window.confirm(confirmCopy(action, optInType))) return
      setApplyBusy(true)
      setApplyError(null)
      setApplyOk(null)
      try {
        const result = await api.applyMaintenanceAction({
          resourceIdentifier: action.resourceIdentifier,
          action: action.action,
          optInType,
        })
        // RDS's own post-apply view. Rendering the refetch instead can show a
        // stale optInStatus and read as though nothing happened.
        const updated = result.pendingActions.find((entry) => entry.action === action.action)
        setApplyOk(
          `${action.action}: ${updated ? describeOptInState(updated).toLowerCase() : 'no longer pending'}`,
        )
        await q.refetch()
      } catch (err) {
        setApplyError(`Failed to update ${action.action}: ${String(err)}`)
      } finally {
        setApplyBusy(false)
      }
    }

    return (
      <>
        <h1>Engine</h1>
        {q.isLoading && <p className="muted">Loading…</p>}
        {q.error && <p role="alert">Failed to load: {String(q.error)}</p>}
        {q.data && (
          <>
            <p className="muted">
              <code>{q.data.clusterIdentifier}</code> · {q.data.engine} · running{' '}
              <code>{q.data.currentVersion}</code>, CDK pins <code>{q.data.pinnedVersion}</code>
              {q.data.versionDrift && (
                <span className="drift-badge">{describeVersionDrift(q.data)}</span>
              )}
            </p>
            {q.data.degraded.length > 0 && (
              <p role="alert">
                <span className="drift-badge">degraded</span> Could not load{' '}
                {q.data.degraded.map(sectionLabel).join(', ')}. Those sections are empty below
                because their reads failed. The version fields above are unaffected.
              </p>
            )}
            {q.data.pendingVersion && (
              <p className="muted">
                Version change in flight: <code>{q.data.pendingVersion}</code>
              </p>
            )}
            <p>{describeNextMaintenance(q.data)}</p>

            <AvailabilitySection />

            <h2 className="section-title">Pending maintenance actions</h2>
            <DegradedNote engine={q.data} section="pendingActions" />
            {applyError && <p role="alert">{applyError}</p>}
            {applyOk && <p className="muted">{applyOk}</p>}
            {q.data.pendingActions.length === 0 ? (
              <p className="muted">None scheduled.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Resource</th>
                    <th>Action</th>
                    <th>Updates to</th>
                    <th>Applies from</th>
                    <th>Forced by</th>
                    <th>Opt-in</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.pendingActions.map((a) => (
                    <tr key={`${a.resourceIdentifier}:${a.action}`}>
                      <td>
                        <code>{a.resourceIdentifier}</code>
                      </td>
                      <td>
                        {a.action}
                        {/* RDS's own words for what the action does, under the
                            bare action name — "system-update" alone never says
                            what is being updated or to what. */}
                        {a.description && <div className="muted">{a.description}</div>}
                      </td>
                      <td>
                        {targetEngineVersion(a) ? <code>{targetEngineVersion(a)}</code> : '—'}
                      </td>
                      <td>{nextApplyDate(a) ?? '—'}</td>
                      <td>{a.forcedApplyDate ?? '—'}</td>
                      <td>{describeOptInState(a)}</td>
                      <td>
                        {/* Disabled as a set while one is in flight, so a
                            double-click cannot send two applies. */}
                        <div className="row-actions">
                          <button
                            disabled={applyBusy}
                            onClick={() => applyOptIn(a, 'next-maintenance')}
                          >
                            Apply at next window
                          </button>
                          <button disabled={applyBusy} onClick={() => applyOptIn(a, 'immediate')}>
                            Apply now
                          </button>
                          {canUndo(a) && (
                            <button
                              disabled={applyBusy}
                              onClick={() => applyOptIn(a, 'undo-opt-in')}
                            >
                              Cancel opt-in
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h2 className="section-title">Available upgrade targets</h2>
            <DegradedNote engine={q.data} section="upgradeTargets" />
            {q.data.upgradeTargets.length === 0 ? (
              <p className="muted">None — this is the newest version offered for this engine.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Description</th>
                    <th>Auto-upgrade</th>
                    <th>Major</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.upgradeTargets.map((t) => (
                    <tr key={t.engineVersion}>
                      <td>
                        <code>{t.engineVersion}</code>
                      </td>
                      <td>{t.description ?? '—'}</td>
                      <td>{t.autoUpgrade ? 'Yes' : 'No'}</td>
                      <td>{t.isMajorVersionUpgrade ? 'Yes' : 'No'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h2 className="section-title">Recorded version history</h2>
            <DegradedNote engine={q.data} section="history" />
            {q.data.history.length === 0 ? (
              <p className="muted">
                Nothing recorded yet — the daily recorder writes a baseline row on its first run.
              </p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Observed</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.history.map((h) => (
                    <tr key={h.observedAt}>
                      <td>{h.observedAt}</td>
                      <td>{h.previousVersion ?? '—'}</td>
                      <td>
                        <code>{h.engineVersion}</code>
                      </td>
                      <td>{h.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <h2 className="section-title">Recent RDS events</h2>
            <p className="muted">RDS retains cluster events for 14 days only.</p>
            <DegradedNote engine={q.data} section="recentEvents" />
            {q.data.recentEvents.length === 0 ? (
              <p className="muted">No events in the last 14 days.</p>
            ) : (
              <table>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Message</th>
                    <th>Categories</th>
                  </tr>
                </thead>
                <tbody>
                  {q.data.recentEvents.map((e) => (
                    <tr key={`${e.date}:${e.message}`}>
                      <td>{e.date}</td>
                      <td>{e.message}</td>
                      <td>{e.categories.join(', ') || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            <LogFilesSection />
          </>
        )}
      </>
    )
  },
})

const routeTree = rootRoute.addChildren([
  indexRoute,
  databasesRoute,
  environmentsRoute,
  engineRoute,
  dumpsRoute,
  sessionsRoute,
])

export const router = createRouter({ routeTree })

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export {
  formatBytes,
  describeNextMaintenance,
  describeVersionDrift,
  nextApplyDate,
  targetEngineVersion,
  describeOptInState,
  canUndo,
  confirmCopy,
  formatPercent,
  formatDuration,
  uptimeWindowLabel,
  describeCoverage,
}
